import { Form, Formik } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { Divider } from '@hivebrite/design-system/Divider';
import { useToggle } from '@hivebrite/react-hooks';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import PromoCodeValidation from 'hb-react/shared/apps/Events/components/EventRegistrationContainer/components/PromoCode/PromoCodeValidation';
import { getInitialValuesStep3 } from 'hb-react/shared/apps/Events/components/EventRegistrationContainer/form/initialValues';
import { PayWhen } from 'hb-react/shared/apps/Events/constants/registrationSteps';
import { useEventCartContext } from 'hb-react/shared/apps/Events/contexts/EventCartContext';
import { useEventContext } from 'hb-react/shared/apps/Events/contexts/EventContext';
import { useRegistrationFlowContext } from 'hb-react/shared/apps/Events/contexts/RegistrationFlowContext';
import ErrorFocus from 'hb-react/shared/components/formik/components/ErrorFocus';
import { LockScreen } from 'hb-react/shared/components/LockScreen';
import UpdateInformationModal from 'hb-react/shared/components/User/UpdateInformationModal';
import { useUserDataSubscriber } from 'hb-react/shared/components/User/UpdateInformationModal/hooks/useUserDataSubscriber';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

import BackButton from './BackButton';
import BillingAndShippingAddress from './BillingAndShippingAddress';
import FreeButton from './FreeButton';
import { useOnFreeTickets } from './hooks/useOnFreeTickets';
import { useOnPayLater } from './hooks/useOnPayLater';
import { useOnPayNow } from './hooks/useOnPayNow';
import { BillingBlock } from './Layout';
import PayButtons from './PayButtons';
import PersonalInformation from './PersonalInformation';
import ShippingAddressForm from './ShippingAddress';
import Navigation from '../Navigation';
import PaymentStep from '../Payment/PaymentStep';
import { ReCaptcha } from '../ReCaptcha';

const BillingInformationStep = () => {
  const [isCompleted, setCompleted] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showPayButton] = useState(true);
  const { isOpen } = useToggle();
  const navigate = useNavigate();
  const { flagEvPaymentsMovePromocode } = useFeatureFlag();
  const {
    currentNetwork: { user_fields_config: userFieldsConfig },
    currentUser,
  } = useDefaultAppContexts();
  const { postPayLater } = useOnPayLater();
  const { postFreeTickets } = useOnFreeTickets();
  const { postPayNow } = useOnPayNow();

  const { event } = useEventContext();
  const { data, isLoading = false } = useEventCartContext();

  const eligibleForPromocodes =
    data?._metadata?.eligible_for_promocodes ?? false;

  const { totalPrice, valuesStep2, valuesStep3, setValuesStep3, isShippable } =
    useRegistrationFlowContext();

  const payWhen = useRef(null);

  const shouldDisplayBillingSection = totalPrice > 0;

  const initialValues = useMemo(
    () =>
      getInitialValuesStep3({
        currentUser,
        values: valuesStep3,
        event,
        isShippable,
      }),
    [currentUser, valuesStep3, isShippable],
  );

  const { changeSet } = useUserDataSubscriber({
    user: currentUser,
    formState: {
      ...valuesStep3?.receipt,
      mobile_perso:
        valuesStep2?.network_events_booking?.attendees_attributes.find(
          ({ user_id: userId }) => userId === currentUser?.id,
        )?.mobile_phone,
    },
  });

  useEffect(() => {
    if (isCompleted) {
      onPurchaseSuccess();
    }
  }, [isCompleted]);

  const onSuccess = () => navigate('../success');

  const onPurchaseSuccess = () => {
    onSuccess();
  };

  const onPaylater = async (formData) => {
    await postPayLater({ formData, onSuccess: () => setCompleted(true) });
  };

  const onFreeTickets = async (formData) => {
    await postFreeTickets({ formData, onSuccess: () => setCompleted(true) });
  };

  const onPayNow = async (formData) => {
    await postPayNow({
      formData,
      onSuccess: () => navigate('../payment'),
    });
  };

  const isPaypalPayment = event.payment_gateway === 'Paypal';

  const onSubmit = async (values) => {
    setValuesStep3(values);

    if (payWhen.current === PayWhen.PAY_NOW) {
      await onPayNow(values);
    } else if (payWhen.current === PayWhen.PAY_LATER) {
      await onPaylater(values);
    } else if (payWhen.current === PayWhen.PAY_NEVER) {
      await onFreeTickets(values);
    } else {
      throw new Error(`unknow "payWhen" value, got: ${payWhen.current}`);
    }
    payWhen.current = null;
  };

  return (
    <>
      {isLoading && <LockScreen />}
      <Box display="block" data-testid="event-registration-form-billing">
        <Formik initialValues={initialValues} onSubmit={onSubmit}>
          {(formik) => {
            const { isSubmitting, setFieldValue, values, submitForm } = formik;

            return (
              <Form>
                <PersonalInformation userFieldsConfig={userFieldsConfig} />
                {shouldDisplayBillingSection && (
                  <BillingAndShippingAddress
                    setFieldValue={setFieldValue}
                    userFieldsConfig={userFieldsConfig}
                    values={values}
                    valuesStep3={valuesStep3}
                  />
                )}

                {isShippable && !shouldDisplayBillingSection && (
                  <BillingBlock>
                    <ShippingAddressForm />
                  </BillingBlock>
                )}

                {eligibleForPromocodes && !flagEvPaymentsMovePromocode && (
                  <PromoCode />
                )}

                <ReCaptcha />

                <Navigation>
                  {({ isFixed, ButtonToggleModal, breakpoint }) => (
                    <Box width="100%" justifyContent="space-between">
                      <LeftButtonsWrapper>
                        {isFixed && <ButtonToggleModal />}
                        <BackButton isFixed={isFixed} values={values} />
                      </LeftButtonsWrapper>
                      <Box pl={2} alignItems="flex-start">
                        {totalPrice > 0 && showPayButton && (
                          <PayButtons
                            isFixed={isFixed}
                            breakpoint={breakpoint}
                            event={event}
                            isSubmitting={isSubmitting}
                            payWhen={payWhen}
                            submitForm={submitForm}
                          />
                        )}
                        {totalPrice > 0 && isPaypalPayment && showPayment && (
                          <PaymentStep
                            onPaymentSuccess={onPurchaseSuccess}
                            onPaymentCancel={() => setShowPayment(true)}
                          />
                        )}
                        {totalPrice === 0 && (
                          <FreeButton
                            isSubmitting={isSubmitting}
                            payWhen={payWhen}
                            submitForm={submitForm}
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </Navigation>
                <ErrorFocus />
              </Form>
            );
          }}
        </Formik>
      </Box>
      {showPayment && !isPaypalPayment && (
        <PaymentStep
          onPaymentSuccess={() => {
            setCompleted(true);
          }}
          onPaymentCancel={() => setShowPayment(false)}
        />
      )}
      {isOpen && (
        <UpdateInformationModal
          changeSet={changeSet}
          onClose={onSuccess}
          onSuccess={onSuccess}
          isOpen
        />
      )}
    </>
  );
};

export default BillingInformationStep;

const CardHeader = ({ children }) => (
  <>
    <Box font="h3-semibold" mb={3}>
      {children}
    </Box>
    <Divider borderBottomColor="horizon" mt={0} mb={5} width="100%" />
  </>
);

const PromoCode = () => (
  <Card bg="white" mb={5} pt={4} pb={2}>
    <CardHeader>{s__('Events|Promo code')}</CardHeader>
    <PromoCodeValidation />
  </Card>
);

const LeftButtonsWrapper = styled(Box).attrs({
  alignItems: 'flex-start',
  flexDirection: { _: 'column', m: 'unset' },
  width: { m: 'inherit' },
  justifyContent: 'space-between',
})``;
